import {
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { PopoverController } from '@ionic/angular';
import { parseISO } from 'date-fns';
import Swal from 'sweetalert2/dist/sweetalert2.all.js';
import { Orders } from 'src/app/models/Orders.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { Status } from 'src/app/models/status';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import { SearchService } from 'src/app/services/search.service';
import { AlertsService } from 'src/app/services/shared/alert-service.service';
import { ChangeOperatorComponent } from './change-operator/change-operator.component';
import { Subscription, interval } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * Lista principale "Prepara Ordine".
 * - Vista operatore: ordini aperti pronti per la preparazione magazzino.
 * - Polling auto-refresh ogni 60s (interval RxJS, auto-cleanup via
 *   takeUntilDestroyed — sostituisce setInterval legacy).
 * - KPI banner 5+2 cards responsive (Diakonix pattern, esteso da v2.10.0 I.1).
 * - Filtri: search testuale (persisted in SearchService) + date range nativo.
 * - Per-row actions: dettagli (route /prepare-order/:id), cambia operatore
 *   (PopoverController Ionic), cancella (sub-modale), blocca/sblocca.
 */
@Component({
  selector: 'app-preparazione-ordini',
  templateUrl: './preparazione-ordini.component.html',
  styleUrls: ['./preparazione-ordini.component.scss'],
  standalone: false,
})


export class PreparazioneOrdiniComponent implements OnInit, OnDestroy {

  // === DI ===
  private destroyRef = inject(DestroyRef);
  private ordersService = inject(OrdersService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private popoverController = inject(PopoverController);
  private alertsService = inject(AlertsService);
  private filterService = inject(SearchService);
  public openModal = inject(OpenModalService); // public per template
  private cdr = inject(ChangeDetectorRef);

  // === Stato lista ===
  public gridData: Orders.IOrderOpenList[] = [];
  public loading = false;
  public filterTerm = '';
  public selectedPrep: string | null = null;
  public orderToSend: Orders.IOrderOpenList | null = null;

  // === Filtri date (Date → string bridge per <input type="date">) ===
  public readonly defaultStartDate = new Date(2021, 0, 1);
  public filters: {
    startingDate: Date | null;
    endDate: Date | null;
    dateOrginizedBy: { text: string; value: string };
  } = {
      startingDate: this.defaultStartDate,
      endDate: new Date(),
      dateOrginizedBy: { text: 'Data ordine', value: 'or_order_number' },
    };

  // === Polling ===
  private readonly REFRESH_INTERVAL_MS = 60_000;
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingSub?: Subscription;

  // === KPI metrics ===
  public netValue = 0;
  public etValuePartial = 0;
  public etValueTotal = 0;
  public etValueAssigned = 0;
  public etValueWorking = 0;
  public etValueSuspended = 0;
  public etValueBlocked = 0;

  public completeOrdersCount = 0;
  public partialOrdersCount = 0;
  public assignedOrdersCount = 0;
  public workingOrdersCount = 0;
  public suspendedOrdersCount = 0;
  public blockedOrdersCount = 0;

  public completeOrdersPercentageCount = 0;
  public partialOrdersPercentageCount = 0;
  public assignedOrdersPercentageCount = 0;
  public workingOrdersPercentageCount = 0;
  public suspendedOrdersPercentageCount = 0;
  public blockedOrdersPercentageCount = 0;

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  /** order_id con blocca/sblocca in corso: previene doppio click. */
  private statusTogglingIds = new Set<Orders.IOrderOpenList['order_id']>();

  /**
   * 🚨 FIX v2.12.6: `findUnblockTargetStatus` ricava lo status "precedente"
   * scansionando `order.status[1..n]` per il primo cod != 101/100. Ma
   * durante la fase di lavorazione il BE non crea sempre una riga history
   * distinta per ogni sub-step (2-5) — spesso resta sulla riga cod=10
   * finché non cambia stato in modo netto → lo scan trova 10 anche quando
   * lo status reale immediatamente precedente al blocco era un altro
   * (es. assegnato=1 o un sub-step picking). Fix: memorizza lo status
   * corrente al momento del blocco (certo al 100%, è quello visualizzato
   * in riga) e usalo come target di sblocco, bypassando lo scan history.
   * `gridData` viene rimpiazzato ad ogni polling/reload quindi non può
   * vivere sull'oggetto `order` — Map separata keyed by order_id.
   */
  private preBlockStatus = new Map<number, number>();


  ngOnInit(): void {
    // Se SearchService ha già un filterText (navigato da altra pagina),
    // applicalo subito senza partire dal polling.
    const persistedSearch = this.filterService.getFilterText();
    if (persistedSearch) {
      this.filterTerm = persistedSearch;
      this.searchOrders(persistedSearch);
      return;
    }

    this.route.queryParams
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((params) => {
        if (Object.keys(params).length === 0) {
          this.startPolling();
          return;
        }
        const dateParam = params['data'];
        this.filters.startingDate = dateParam ? parseISO(dateParam) : null;
        this.searchOrders(params['search'] ?? '');
        this.cdr.detectChanges();
      });
  }

  /**
   * Avvia il polling auto-refresh. `interval()` + takeUntilDestroyed
   * sostituisce il legacy `setInterval` (che veniva cleared con
   * clearTimeout — semantic mismatch). Cleanup automatico a destroy.
   */
  private startPolling(): void {
    this.stopPolling();
    this.loadOrders();
    this.pollingSub = interval(this.REFRESH_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadOrders();
      });
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  loadOrders(): void {
    if (this.loading) return;

    this.loading = true;
    this.ordersService
      .getOpenOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders) => {
          this.gridData = orders ?? [];
          this.applyMetrics();
          this.applyClientFilters();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /**
   * Aggrega KPI sui dati correnti. Chiamato SOLO dopo il subscribe,
   * mai in init class (fix bug latente: reduce su array vuoto).
   *
   * Logica KPI cards (richiesto da PM):
   *  - Totale = somma di tutti gli ordini
   *  - Completi + Parziali = Totale (split per or_partial)
   *  - Assegnati + In Lavorazione + Sospesi + Bloccati = Totale (split per status)
   * Per garantire la somma 100% per status, "In Lavorazione" copre tutti i
   * sub-stati di lavorazione attiva (cod 2-5 + 10) — non solo cod 10.
   * "Assegnati" cattura anche gli stati legacy non-mappati come fallback.
   */
  private applyMetrics(): void {
    this.resetMetrics();
    if (!this.gridData?.length) return;

    for (const order of this.gridData) {
      const value = order.or_net_value || 0;
      const cod = order.tab_order_status_cod;
      this.netValue += value;

      // Split or_partial → Completi + Parziali = Totale.
      if (order.or_partial === 'S') {
        this.etValuePartial += value;
      } else {
        this.etValueTotal += value;
      }

      // Split status → Assegnati + In Lavorazione + Sospesi + Bloccati = Totale.
      if (cod === 101 || (order as any)?.or_status === 'B') {
        this.etValueBlocked += value;
      }
      else if (cod === 100) {
        this.etValueSuspended += value;
      }
      else if (cod >= 2 && cod <= 10) {
        // In lavorazione: sub-stati picking (2-5) + working (10).
        this.etValueWorking += value;
      }
      else {
        // Assegnato: cod 1 + qualsiasi altro stato legacy non mappato
        // (es. cod 0/6/7/11) fallback per garantire somma totale.
        this.etValueAssigned += value;
      }
    }

    this.calculateOrderCounts();
    this.calculatePercentages();
  }

  private resetMetrics(): void {
    this.netValue = 0;
    this.etValuePartial = 0;
    this.etValueTotal = 0;
    this.etValueAssigned = 0;
    this.etValueWorking = 0;
    this.etValueSuspended = 0;
    this.etValueBlocked = 0;
  }

  private calculateOrderCounts(): void {
    // Allineato con applyMetrics: Completi + Parziali = Totale,
    // Assegnati + In Lavorazione + Sospesi + Bloccati = Totale.
    this.partialOrdersCount = this.gridData.filter((o) => o.or_partial === 'S').length;
    this.completeOrdersCount = this.gridData.length - this.partialOrdersCount;

    this.blockedOrdersCount = this.gridData.filter(
      (o) => o.tab_order_status_cod === 101 || (o as any)?.or_status === 'B',
    ).length;
    this.suspendedOrdersCount = this.gridData.filter(
      (o) => o.tab_order_status_cod === 100,
    ).length;
    this.workingOrdersCount = this.gridData.filter((o) => {
      const cod = o.tab_order_status_cod;
      return cod >= 2 && cod <= 10 && cod !== 100 && cod !== 101;
    }).length;
    this.assignedOrdersCount =
      this.gridData.length -
      this.blockedOrdersCount -
      this.suspendedOrdersCount -
      this.workingOrdersCount;
  }

  private calculatePercentages(): void {
    const total = this.gridData.length;
    this.completeOrdersPercentageCount = this.pct(this.completeOrdersCount, total);
    this.partialOrdersPercentageCount = this.pct(this.partialOrdersCount, total);
    this.assignedOrdersPercentageCount = this.pct(this.assignedOrdersCount, total);
    this.workingOrdersPercentageCount = this.pct(this.workingOrdersCount, total);
    this.suspendedOrdersPercentageCount = this.pct(this.suspendedOrdersCount, total);
    this.blockedOrdersPercentageCount = this.pct(this.blockedOrdersCount, total);
  }

  private pct(num: number, den: number): number {
    return den > 0 ? (num / den) * 100 : 0;
  }

  // === Search ===

  onSearchInput(): void {
    this.filterService.setFilterText(this.filterTerm);
  }

  searchOrders(term?: string): void {
    if (term !== undefined) this.filterTerm = term;
    if (!this.filterTerm?.trim()) {
      this.startPolling();
      return;
    }
    // Ricerca attiva: ferma il polling, altrimenti loadOrders() al tick
    // successivo rimpiazzerebbe i risultati filtrati con la lista completa.
    this.stopPolling();
    this.loading = true;
    this.ordersService
      .searchOpenOrders(this.filterTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: IWsResult) => {
        this.gridData = res?.data ?? [];
        this.applyMetrics();
        this.applyClientFilters();
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.filters.startingDate = this.defaultStartDate;
    this.filterService.clearFilterText();
    this.startPolling();
  }

  // === Date filter bridge per <input type="date"> ===
  get startDateStr(): string {
    return this.toDateStr(this.filters.startingDate);
  }
  set startDateStr(s: string) {
    this.filters.startingDate = s ? new Date(s) : null;
    this.applyClientFilters();
  }
  get endDateStr(): string {
    return this.toDateStr(this.filters.endDate);
  }
  set endDateStr(s: string) {
    this.filters.endDate = s ? new Date(s) : null;
    this.applyClientFilters();
  }
  private toDateStr(d: Date | null): string {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // === Actions ===

  /**
   * Refresh post-azione (block/unblock/cambio operatore): se c'è una
   * ricerca attiva ri-esegue la search (mantiene il filtro), altrimenti
   * ricarica la lista completa.
   */
  private refreshList(): void {
    if (this.filterTerm?.trim()) {
      this.searchOrders();
    } else {
      this.loadOrders();
    }
  }

  /**
   * True se l'ordine è bloccato. Check robusto su 3 colonne (il backend
   * legacy popola in modo non uniforme: alcuni endpoint settano solo
   * `tab_order_status_cod=101`, altri solo `or_status='B'`).
   * Stesso pattern di v2.10.0 assegna-ordine-list (Chunk I.1).
   */
  isBlocked(order: Orders.IOrderOpenList): boolean {
    return (
      order?.tab_order_status_cod === 101 ||
      (order as any)?.or_status === 'B' ||
      order?.tab_order_status_desc === 'Blocca'
    );
  }

  gotoDetails(orderId: number | string): void {
    this.router.navigate(['/prepare-order', orderId]);
  }

  confirmCancel(order: Orders.IOrderOpenList): void {
    this.orderToSend = order;
    this.openModal.onConfirmCancel();
  }

  /**
   * Blocca ordine: setStatus → blocked (101) + aggiornamento in-memory
   * immediato (cod + status + desc) per UX reattiva senza attendere il
   * polling. Stesso pattern di v2.10.0 I.1.
   */
  isStatusToggling(order: Orders.IOrderOpenList): boolean {
    return this.statusTogglingIds.has(order.order_id);
  }

  block(order: Orders.IOrderOpenList): void {
    if (this.statusTogglingIds.has(order.order_id)) return;
    this.statusTogglingIds.add(order.order_id);
    this.preBlockStatus.set(order.order_id, order.tab_order_status_cod);
    this.ordersService
      .setStatus(order.order_id, Status.StatusesUser.blocked)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.statusTogglingIds.delete(order.order_id)),
      )
      .subscribe((res: IWsResult) => {
        if (!res?.success) return;
        order.tab_order_status_cod = 101;
        (order as any).or_status = 'B';
        order.tab_order_status_desc = 'Blocca';
        Swal.fire({
          icon: 'success',
          title: 'Bloccato!',
          text: 'Ordine bloccato con successo.',
          timer: 1500,
          timerProgressBar: true,
        });
        this.refreshList();
      });
  }

  /**
   * Sblocca: ripristina lo stato precedente.
   *
   * 🚨 FIX v2.12.3: pre-v2.12.3 si usava direttamente `status[1]`
   * come previous, ma in alcuni scenari BE (es. dopo cambio operatore)
   * `status[1]` poteva contenere `101 (blocked)` stesso invece dello
   * status reale precedente → `setStatus(101)` no-op → l'ordine
   * restava bloccato anche dopo popup successo. Ora cerco il primo
   * cod nella history che NON sia 101/100, con fallback a `step1=2`.
   * Stesso fix in J.3 main shell (`preparazione-ordini-detaggli`).
   */
  private findUnblockTargetStatus(history: Orders.IOrder['status'] | undefined): number {
    const arr = history ?? [];
    for (let i = 1; i < arr.length; i++) {
      const cod = arr[i]?.os_status_order_cod;
      if (cod != null && cod !== 101 && cod !== 100) {
        return cod;
      }
    }
    return Status.StatusesUser.step1;
  }

  unblock(order: Orders.IOrderOpenList): void {
    if (this.statusTogglingIds.has(order.order_id)) return;
    this.statusTogglingIds.add(order.order_id);

    const savedStatus = this.preBlockStatus.get(order.order_id);
    if (savedStatus != null) {
      this.applyUnblock(order, savedStatus);
      return;
    }
    // Nessun status salvato (es. pagina ricaricata tra block e unblock):
    // fallback allo scan history, best-effort.
    this.ordersService
      .getOrder1(order.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((orderData) => {
        const targetStatus = this.findUnblockTargetStatus(orderData?.status);
        this.applyUnblock(order, targetStatus);
      });
  }

  private applyUnblock(order: Orders.IOrderOpenList, targetStatus: number): void {
    this.ordersService
      .setStatus(order.order_id, targetStatus)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.statusTogglingIds.delete(order.order_id)),
      )
      .subscribe((res: IWsResult) => {
        if (!res?.success) return;
        this.preBlockStatus.delete(order.order_id);
        order.tab_order_status_cod = targetStatus;
        (order as any).or_status = '';
        order.tab_order_status_desc = '';
        Swal.fire({
          icon: 'success',
          title: 'Sbloccato!',
          text: 'Ordine sbloccato con successo.',
          timer: 1500,
          timerProgressBar: true,
        });
        this.refreshList();
      });
  }

  async openChangeOperator(item: Orders.IOrderOpenList): Promise<void> {
    const popover = await this.popoverController.create({
      component: ChangeOperatorComponent,
      componentProps: { itemSelected: item },
      cssClass: 'semi-wide-popover',
    });
    await popover.present();
    await popover.onDidDismiss();
    this.refreshList();
  }

  /**
   * Classe row per tinting in base allo status corrente.
   * Decisione PM: agli operatori interessa solo lo stato dell'ordine
   * (Assegnato/In Lavorazione/Sospeso/Bloccato), non il tipo parziale.
   * Quindi NO row-partial visivo — i parziali continuano a esistere
   * come KPI card a parte ma non colorano la riga.
   *
   * Priorità: blocked > suspended > working > assigned.
   */
  rowClassFor(order: Orders.IOrderOpenList): string {
    if (order?.tab_order_status_cod === 101 || (order as any)?.or_status === 'B') {
      return 'row-blocked';
    }
    if (order?.tab_order_status_cod === 100) {
      return 'row-suspended';
    }
    const cod = order?.tab_order_status_cod;
    if (cod >= 2 && cod <= 10) {
      return 'row-working';
    }
    return 'row-assigned';
  }

  // === Hover popover info prodotti (preservato dal legacy) ===
  onHoverStart(orderId: number): void {
    this.stopHoverTimer();
    this.hoverTimer = setTimeout(() => {
      this.alertsService.presentProductsInfo(orderId);
    }, 1000);
  }
  onHoverEnd(): void {
    this.stopHoverTimer();
  }
  @HostListener('window:blur')
  onWindowBlur(): void {
    this.stopHoverTimer();
  }
  private stopHoverTimer(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopHoverTimer();
    localStorage.removeItem('query-search');
    // Polling subscription auto-pulita da takeUntilDestroyed.
  }

  /**
   * Vista filtrata/ordinata CACHED (perf fix): sostituisce il vecchio
   * getter `filteredOrders` + pipe `byToDate`, che ricostruivano array
   * nuovi ad ogni ciclo di change detection (getter → nuova reference →
   * pure pipe invalidata → doppio re-filter con parseISO per riga ad
   * ogni keystroke/hover/tick). Ora si ricalcola SOLO quando cambiano
   * dati, date, cliente o sort — via applyClientFilters().
   */
  public visibleOrders: Orders.IOrderOpenList[] = [];

  private applyClientFilters(): void {
    const customer = this.selectedPrep;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    let items = this.gridData.filter((o) => {
      if (customer && o.or_customer !== customer) return false;
      if ((start || end) && o.or_order_date) {
        // Il modello dichiara Date ma il BE serve stringhe ISO: gestisci entrambi.
        const raw = o.or_order_date as Date | string;
        const itemDate = raw instanceof Date ? raw : parseISO(raw);
        if (!isNaN(itemDate.getTime())) {
          if (start && itemDate < start) return false;
          if (end && itemDate > end) return false;
        }
      }
      return true;
    });
    if (this.sortColumn) {
      items = sortItems(items, this.sortColumn, this.sortDirection);
    }
    this.visibleOrders = items;
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.applyClientFilters();
    this.cdr.detectChanges();
  }

}
